<script setup lang="ts">

import {ref, computed} from 'vue';
import Article from '@/model/Article';
import {findArticles, getArticle, getArticleStock} from '@/api/module/article';
import ArticleStock, {ApiArticleStock} from '@/model/ArticleStock';
import {useSettings} from '@/store/settings';
import moment from 'moment';
import ArticleStockItem from '@/components/Article/ArticleStockItem.vue';
import Subsidiary from '@/model/Subsidiary';
import {getSubsidiaries, getWarehouses} from '@/api/module/warehouse';
import router from '@/router';
import {useScanner} from '@/store/scanner';
import ErrorDisplay from '@/components/ErrorDisplay.vue';
import Loading from '@/components/Loading.vue';
import Warehouse from '@/model/Warehouse';
import {ArticlePrice} from '@/model/ArticlePrice';
import {useRoute} from 'vue-router';

const route = useRoute();
const scanner = useScanner();
const settings = useSettings();
const selectedTab = ref<string>('info');
const article = ref<null | Article>(null);
const articleStock = ref<null | ArticleStock[]>(null);
const loading = ref(false);
const scanning = ref(false);
const error = ref<Error | null>(null);

const niederlassungen = ref<Subsidiary[]>([]);
const lagerhallen = ref<Warehouse[]>([]);


function loadNiederlassungen() {
  getSubsidiaries().then(([data]) => {
    niederlassungen.value = data;
  });
}

function loadLagerhallen() {
  getWarehouses({Niederlassung: settings.selectedSubsidiary ?? undefined}, []).then(([data]) => {
    lagerhallen.value = data;
  });
}

function getNiederlassungName(niederlassung: number): string {
  return niederlassungen.value.find((s) => s.NiederlassungNr == niederlassung)?.Name1 ?? '';
}

function getLagerhalleName(lagerhalle: number): string {
  return lagerhallen.value.find((s) => s.Lagerhalle == lagerhalle)?.Bezeichnung ?? '';
}

async function loadArticle() {
  if (article.value) return;
  loading.value = true;

  try {
    const [foundArticle] = await getArticle(route.params.id, {mitBeschreibung: true, mitArtikelPreise: true});
    if (!foundArticle) throw new Error('Artikel nicht gefunden');

    article.value = Article.fromApi(foundArticle);
  } catch (err: any) {
    console.log(err);
  } finally {
    loading.value = false;
  }
}

async function jumpToArticle(searchValue: string) {
  loading.value = true;
  try {
    const [foundArticles] = await findArticles(searchValue);

    const articles = foundArticles.map((a) => Article.fromApi(a));

    if (articles.length == 1) {
      if (article.value != null && articles[0].ArtikelNr == article.value.ArtikelNr) return;

      router.replace({name: 'article_info_detail', params: {id: `${articles[0].ArtikelNr}`}}).then(() => {
        console.log(route.params);
        refresh();
      }).catch(console.error);
    } else if (articles.length > 1) {
      router.push({name: 'article_info_list', query: {search: searchValue}}).catch(console.error);
    }
  } catch (err: any) {
    error.value = err as Error;
  } finally {
    loading.value = false;
  }
}

scanner.addScanListener(async (event) => {
  if (scanning.value) return;
  scanning.value = true;

  await jumpToArticle(event.data);

  scanning.value = false;
});

async function loadArticleStock() {
  if (articleStock.value) return;
  try {
    const [result] = await getArticleStock(route.params.id as string);

    articleStock.value = result.map((x) => ArticleStock.fromApi(x));
  } catch (err: any) {
    error.value = err as Error;
  }
}

const bestandAktuelleNiederlassungUndLagerhalle = computed((): ApiArticleStock | undefined => {
  if (!articleStock.value) return undefined;
  if (settings.selectedWarehouse == null) return undefined;

  const warehouse = settings.selectedWarehouse;

  return articleStock.value.find((s) => {
    return s.Niederlassung == warehouse.Niederlassung && s.Lagerhalle == warehouse.Lagerhalle;
  });
});

type NiederlassungGruppe = {
  ArticleStockSummiert: ArticleStock;
  ArticleStocks: ArticleStock[];
}

const bestandNiederlassungGruppiert = computed((): Map<number, NiederlassungGruppe> => {
  const result = new Map<number, NiederlassungGruppe>();
  if (!articleStock.value) return result;

  articleStock.value.forEach((x) => {
    const collection = result.get(x.Niederlassung);

    if (!collection) {
      result.set(x.Niederlassung, {
        ArticleStockSummiert: {
          MengeBestellt: x.MengeBestellt,
          MengeReserviert: x.MengeReserviert,
          BestandPhysikalisch: x.BestandPhysikalisch,
        } as ArticleStock,
        ArticleStocks: [x] as ArticleStock[],
      } as NiederlassungGruppe);
    } else {
      collection.ArticleStockSummiert.BestandPhysikalisch += x.BestandPhysikalisch;
      collection.ArticleStockSummiert.MengeReserviert += x.MengeReserviert;
      collection.ArticleStockSummiert.MengeBestellt += x.MengeBestellt;
      collection.ArticleStocks.push(x);
    }
  });

  return result;
});

const preistabellenGruppiert = computed((): Map<number, ArticlePrice[]> => {
  const result = new Map<number, ArticlePrice[]>();
  if (!article.value) return result;

  article.value.currentPrices.forEach((x) => {
    const collection = result.get(x.PreisTabelleNr);

    if (!collection) {
      result.set(x.PreisTabelleNr, [x]);
    } else {
      collection.push(x);
    }
  });

  return result;
});

function formatCurrency(val: number) {
  // eslint-disable-next-line no-irregular-whitespace
  return `${val.toFixed(2).replaceAll('.', ',')} €`;
}

function refresh() {
  article.value = null;
  articleStock.value = null;

  loadArticle();
  loadArticleStock();
}

loadNiederlassungen();
loadLagerhallen();
refresh();

defineExpose({
  selectedTab,
  article,
  articleStock,
  Loading,
  bestandAktuelleNiederlassungUndLagerhalle,
  bestandNiederlassungGruppiert,
  moment,
  ArticleStockItem,
  getNiederlassungName,
  getLagerhalleName,
  error,
  ErrorDisplay,
  formatCurrency,
  preistabellenGruppiert,
  loading,
});
</script>

<template>
  <div class="article-detail pa-2">
    <ErrorDisplay v-if="error" :message="error.message" :loading="loading" @retry="refresh()"/>
    <template v-if="!loading && !error && article">
      <v-list>
        <v-list-item :title="article.Bezeichnung1" :subtitle="article.Bezeichnung2"/>
        <v-list-item lines="two" class="mt-n3">
          <template v-slot:subtitle>
            Artikel-Nr.
            <span class="float-right">Artikelgruppe</span>
          </template>
          <template v-slot:title>
            {{ article.ArtikelNr }}
            <span class="float-right">
                {{ article.GruppeBezeichnung }}
              </span>
          </template>
        </v-list-item>
        <div v-if="bestandAktuelleNiederlassungUndLagerhalle" class="mt-n3">
          <ArticleStockItem class="mt-n3" :article-stock="bestandAktuelleNiederlassungUndLagerhalle"/>
        </div>
      </v-list>
      <v-card>
        <v-tabs v-model="selectedTab" fixed-tabs>
          <v-tab tab-value="info">Info</v-tab>
          <v-tab tab-value="bestand">Bestand</v-tab>
          <v-tab tab-value="preise">Preise</v-tab>
        </v-tabs>
        <v-window v-model="selectedTab">
          <v-window-item value="info">
            <v-card flat>
              <v-list>
                <v-list-item lines="two" class="mt-n3">
                  <v-list-item-subtitle>
                    Einheit
                    <span class="float-right">Preiseinheit</span>
                  </v-list-item-subtitle>
                  <v-list-item-title>
                    {{ article.EinheitBezeichnungLang }} ({{ article.EinheitBezeichnung }})
                    <span class="float-right">
                          {{ article.PE1 }}
                        </span>
                  </v-list-item-title>
                </v-list-item>
                <v-list-item lines="two" class="mt-n3">
                  <v-list-item-subtitle>
                    EAN
                  </v-list-item-subtitle>
                  <v-list-item-title>
                    {{ article.EANCode }}
                  </v-list-item-title>
                </v-list-item>
                <v-list-item lines="two" class="mt-n3">
                  <v-list-item-subtitle>
                    Beschreibung
                  </v-list-item-subtitle>
                  <div v-html="article.getBeschreibung()"/>
                </v-list-item>
                <template v-if="article.HauptGebinde">
                  <v-list-item lines="two" class="mt-n3">

                    <v-list-item-subtitle>
                      Haupt-Gebinde
                      <span class="float-right">Inhalt</span>
                    </v-list-item-subtitle>
                    <v-list-item-title>
                      {{ article.HauptGebinde.GebindeBezeichnung }}
                      <span class="float-right">
                          {{ article.HauptGebinde.GebindeInhalt }}
                          {{ article.EinheitBezeichnungLang }}
                        </span>
                    </v-list-item-title>

                  </v-list-item>
                </template>
                <v-list-item lines="two" class="mt-n3">

                  <v-list-item-subtitle>
                    Letzter Einkauf
                    <span class="float-right">Letzter Verkauf</span>
                  </v-list-item-subtitle>
                  <v-list-item-title>
                    {{ moment(article.LetztesEKDatum).format('DD.MM.YYYY') }}
                    <span class="float-right">
                          {{ moment(article.LetztesVKDatum).format('DD.MM.YYYY') }}
                      </span>
                  </v-list-item-title>

                </v-list-item>
              </v-list>
            </v-card>
          </v-window-item>
          <v-window-item value="bestand">
            <v-card flat>
              <v-list>
                <template v-for="[niederlassung, niederlassungGruppiert] in bestandNiederlassungGruppiert"
                          :key="niederlassung">
                  <v-list-group>
                    <template v-slot:activator="{ props }">
                      <v-list-item v-bind="props" :title="getNiederlassungName(niederlassung)"
                                   subtitle="Niederlassung" append-icon="">
                        <ArticleStockItem :article-stock="niederlassungGruppiert.ArticleStockSummiert"/>
                        <div class="text-center">
                          <v-icon>$expand</v-icon>
                        </div>
                      </v-list-item>
                    </template>
                    <div v-for="articleStock in niederlassungGruppiert.ArticleStocks"
                         :key="`${niederlassung}_${articleStock.Lagerhalle}`" class="bg-grey-lighten-2">
                      <v-list-item class="mb-3">
                        <v-list-item-subtitle>Lagerhalle</v-list-item-subtitle>
                        <v-list-item-title>{{ getLagerhalleName(articleStock.Lagerhalle) }}</v-list-item-title>
                      </v-list-item>
                      <div class="px-3">
                        <ArticleStockItem :article-stock="articleStock"/>
                      </div>
                      <hr
                        role="separator"
                        aria-orientation="horizontal"
                        class="v-divider theme--light"
                      />
                    </div>
                  </v-list-group>
                  <hr
                    role="separator"
                    aria-orientation="horizontal"
                    class="v-divider theme--light"
                  />
                </template>
              </v-list>
            </v-card>
          </v-window-item>
          <v-window-item value="preise">
            <v-card flat>
              <v-list>
                <template v-for="[preistabelleNr, preise] in preistabellenGruppiert" :key="preistabelleNr">
                  <v-list-group active-color="">
                    <template v-slot:activator="{ isOpen, props }">
                      <v-list-item v-bind="props" :title="`Preistabelle ${preistabelleNr}`"
                                   :class="isOpen ? 'bg-grey-lighten-2' : ''">
                        <v-list-item-title>Preistabelle {{ preistabelleNr }}</v-list-item-title>
                        <v-list-item-subtitle>
                          Gültig
                          <span class="float-right">Kundengruppe</span>
                        </v-list-item-subtitle>
                        <v-list-item-title>
                          {{ preise[0].dateString }}
                          <span class="float-right">{{ preise[0].KundenGruppe }}</span>
                        </v-list-item-title>
                        <div class="text-center">
                          <v-icon>$expand</v-icon>
                        </div>
                      </v-list-item>
                    </template>
                    <div class="bg-grey-lighten-2" v-for="price in preise" :key="price.key">
                      <v-list-item lines="two">
                        <template v-slot:subtitle>
                          <div v-if="price.qtyString">
                            {{ price.qtyString }}&nbsp;{{ article.EinheitBezeichnung }}
                          </div>
                          <div>
                            netto
                            <span class="float-right">brutto</span>
                          </div>
                        </template>
                        <template v-slot:title>
                          {{ formatCurrency(price.Netto) }}
                          <span class="float-right">{{ formatCurrency(price.Brutto) }}</span>
                        </template>
                      </v-list-item>
                      <hr
                        role="separator"
                        :key="`${price.PreisTabelleNr}-${price.AbMenge}-hr`"
                        aria-orientation="horizontal"
                        class="v-divider theme--light"
                      />
                    </div>
                  </v-list-group>
                </template>
              </v-list>
            </v-card>
          </v-window-item>
        </v-window>
      </v-card>
    </template>
    <template v-else>
      <loading/>
    </template>
  </div>
</template>

<style scoped>
</style>
